import { useQuery } from '@tanstack/react-query';
import { type JSX } from 'react';
import { Link, useSearchParams } from 'react-router';
import { adminUsageApi, type AdminUsageDto, type AdminUsageRange } from '../api/admin-usage.js';
import { useAuth } from '../auth/context.js';
import { useI18n } from '../i18n/context.js';

export function AdminUsagePage(): JSX.Element {
  const { user } = useAuth();
  const { t, formatDate, locale } = useI18n();
  const range = useUsageRange();
  const usage = useQuery({
    queryKey: ['admin-usage', range.value],
    queryFn: () => adminUsageApi.summary(range.value),
    enabled: user?.role === 'admin',
    refetchInterval: 5_000,
  });

  if (user?.role !== 'admin') {
    return (
      <main className="mx-auto max-w-3xl p-6">
        <p role="alert">{t.admin.forbidden}</p>
        <Link to="/app" className="kitup-quiet-button mt-4">
          {t.admin.toApp}
        </Link>
      </main>
    );
  }

  const data = usage.data;
  return (
    <main className="mx-auto max-w-[1500px] p-4 sm:p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t.admin.usage.title}</h1>
          <p className="text-sm text-[var(--kitup-color-muted)]">{t.admin.usage.subtitle}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/book-builder" className="kitup-quiet-button">
            {t.admin.bookBuilder.title}
          </Link>
          <Link to="/admin/accounts" className="kitup-quiet-button">
            {t.admin.accounts.title}
          </Link>
          <Link to="/admin/generations" className="kitup-quiet-button">
            {t.admin.title}
          </Link>
          <Link to="/admin/promotions" className="kitup-quiet-button">
            {t.admin.promotions.title}
          </Link>
          <Link to="/app" className="kitup-quiet-button">
            {t.admin.toApp}
          </Link>
        </div>
      </div>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-[var(--kitup-color-muted)]/25 p-1">
          {(['day', 'week'] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={range.value === value}
              className="rounded-md px-3 py-1.5 text-sm aria-pressed:bg-[var(--kitup-color-ink)] aria-pressed:text-white"
              onClick={() => range.set(value)}
            >
              {t.admin.usage.ranges[value]}
            </button>
          ))}
        </div>
        {data ? (
          <p className="flex items-center gap-2 text-xs text-[var(--kitup-color-muted)]">
            <span
              className="h-2 w-2 animate-pulse rounded-full bg-emerald-500"
              aria-hidden="true"
            />
            {t.admin.usage.liveUpdated(formatDate(data.updatedAt))}
          </p>
        ) : null}
      </div>

      {usage.isPending ? <p>{t.common.loading}</p> : null}
      {usage.isError ? <p role="alert">{t.common.unexpectedError}</p> : null}
      {data ? (
        <>
          <LiveStrip data={data} />
          <section
            aria-label={t.admin.usage.summary}
            className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-6"
          >
            <Metric label={t.admin.usage.total} value={integer(data.summary.total, locale)} />
            <Metric
              label={t.admin.usage.regenerations}
              value={integer(data.summary.regenerations, locale)}
              detail={percentOf(data.summary.regenerations, data.summary.total, locale)}
            />
            <Metric
              label={t.admin.usage.users}
              value={integer(data.summary.uniqueUsers, locale)}
              detail={t.admin.usage.booksDetail(data.summary.uniqueBooks)}
            />
            <Metric
              label={t.admin.usage.successRate}
              value={data.summary.successRate.toLocaleString(locale, {
                style: 'percent',
                maximumFractionDigits: 1,
              })}
              detail={t.admin.usage.failedDetail(data.summary.failed)}
              tone={data.summary.failed > 0 ? 'red' : 'plain'}
            />
            <Metric label={t.admin.usage.cost} value={decimal(data.summary.costUnits, locale)} />
            <Metric
              label={t.admin.usage.averageLatency}
              value={latency(data.summary.averageLatencyMs, locale)}
              detail={t.admin.usage.adminReplaysDetail(data.summary.adminReplays)}
            />
          </section>

          <section className="kitup-card mb-5 p-4 sm:p-5" aria-labelledby="usage-timeline">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
              <div>
                <h2 id="usage-timeline" className="font-semibold">
                  {t.admin.usage.timeline}
                </h2>
                <p className="text-xs text-[var(--kitup-color-muted)]">
                  {formatDate(data.from)} — {formatDate(data.to)}
                </p>
              </div>
              <div className="flex flex-wrap gap-4 text-xs">
                <Legend color="bg-violet-300" label={t.admin.usage.legend.total} />
                <Legend color="bg-rose-500" label={t.admin.usage.legend.failed} />
                <Legend color="bg-amber-500" label={t.admin.usage.legend.regenerations} line />
              </div>
            </div>
            <TimelineChart data={data} locale={locale} />
          </section>

          <div className="grid gap-5 lg:grid-cols-[1fr_1.2fr]">
            <TargetBreakdown data={data} />
            <TopUsers data={data} />
          </div>
        </>
      ) : null}
    </main>
  );
}

function useUsageRange(): { value: AdminUsageRange; set(value: AdminUsageRange): void } {
  const [searchParams, setSearchParams] = useSearchParams();
  const value: AdminUsageRange = searchParams.get('range') === 'week' ? 'week' : 'day';
  return {
    value,
    set(next) {
      setSearchParams({ range: next }, { replace: true });
    },
  };
}

function LiveStrip({ data }: { data: AdminUsageDto }): JSX.Element {
  const { t, locale } = useI18n();
  return (
    <section
      className="kitup-card mb-5 flex flex-wrap items-center gap-x-8 gap-y-3 p-4"
      aria-label={t.admin.usage.live}
    >
      <div>
        <p className="text-xs text-[var(--kitup-color-muted)]">{t.admin.usage.runningNow}</p>
        <p className="text-2xl font-semibold text-emerald-700">
          {integer(data.live.running, locale)}
        </p>
      </div>
      <div>
        <p className="text-xs text-[var(--kitup-color-muted)]">{t.admin.usage.queuedNow}</p>
        <p className="text-2xl font-semibold">{integer(data.live.queued, locale)}</p>
      </div>
      <p className="text-sm text-[var(--kitup-color-muted)]">{t.admin.usage.refreshHint}</p>
    </section>
  );
}

function Metric({
  label,
  value,
  detail,
  tone = 'plain',
}: {
  label: string;
  value: string;
  detail?: string;
  tone?: 'plain' | 'red';
}): JSX.Element {
  return (
    <div className="kitup-card p-4">
      <p className="text-xs text-[var(--kitup-color-muted)]">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${tone === 'red' ? 'text-rose-700' : ''}`}>
        {value}
      </p>
      {detail ? <p className="mt-1 text-xs text-[var(--kitup-color-muted)]">{detail}</p> : null}
    </div>
  );
}

function TimelineChart({ data, locale }: { data: AdminUsageDto; locale: string }): JSX.Element {
  const { t } = useI18n();
  const width = 760;
  const height = 250;
  const margin = { left: 42, right: 14, top: 12, bottom: 36 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const maximum = Math.max(1, ...data.timeline.map((item) => item.total));
  const step = plotWidth / Math.max(1, data.timeline.length);
  const barWidth = Math.max(4, step * 0.64);
  const y = (value: number): number => margin.top + plotHeight - (value / maximum) * plotHeight;
  const linePoints = data.timeline
    .map((item, index) => `${margin.left + step * (index + 0.5)},${y(item.regenerations)}`)
    .join(' ');
  const ticks = [0, Math.ceil(maximum / 2), maximum].filter(
    (value, index, values) => values.indexOf(value) === index,
  );
  const labelEvery = data.range === 'day' ? 4 : 1;
  const formatter = new Intl.DateTimeFormat(
    locale,
    data.range === 'day' ? { hour: '2-digit' } : { weekday: 'short', day: 'numeric' },
  );

  return (
    <div className="overflow-x-auto">
      <svg
        role="img"
        aria-label={t.admin.usage.chartLabel}
        viewBox={`0 0 ${width} ${height}`}
        className="min-w-[620px]"
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={margin.left}
              x2={width - margin.right}
              y1={y(tick)}
              y2={y(tick)}
              stroke="currentColor"
              opacity="0.12"
            />
            <text
              x={margin.left - 8}
              y={y(tick) + 4}
              textAnchor="end"
              fontSize="11"
              fill="currentColor"
            >
              {tick}
            </text>
          </g>
        ))}
        {data.timeline.map((item, index) => {
          const x = margin.left + step * index + (step - barWidth) / 2;
          const totalY = y(item.total);
          const failedHeight = (item.failed / maximum) * plotHeight;
          const showLabel = index % labelEvery === 0 || index === data.timeline.length - 1;
          return (
            <g key={item.start}>
              <rect
                x={x}
                y={totalY}
                width={barWidth}
                height={margin.top + plotHeight - totalY}
                rx="2"
                className="fill-violet-300"
              >
                <title>
                  {t.admin.usage.bucketTooltip(
                    formatter.format(new Date(item.start)),
                    item.total,
                    item.regenerations,
                    item.failed,
                  )}
                </title>
              </rect>
              {failedHeight > 0 ? (
                <rect
                  x={x}
                  y={margin.top + plotHeight - failedHeight}
                  width={barWidth}
                  height={failedHeight}
                  rx="2"
                  className="fill-rose-500"
                />
              ) : null}
              {showLabel ? (
                <text
                  x={margin.left + step * (index + 0.5)}
                  y={height - 12}
                  textAnchor="middle"
                  fontSize="11"
                  fill="currentColor"
                >
                  {formatter.format(new Date(item.start))}
                </text>
              ) : null}
            </g>
          );
        })}
        <polyline
          points={linePoints}
          fill="none"
          className="stroke-amber-500"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        {data.timeline.map((item, index) => (
          <circle
            key={`point-${item.start}`}
            cx={margin.left + step * (index + 0.5)}
            cy={y(item.regenerations)}
            r="3"
            className="fill-amber-500"
          >
            <title>{t.admin.usage.regenerationPoint(item.regenerations)}</title>
          </circle>
        ))}
      </svg>
    </div>
  );
}

function Legend({
  color,
  label,
  line = false,
}: {
  color: string;
  label: string;
  line?: boolean;
}): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={`${color} ${line ? 'h-0.5 w-4' : 'h-2.5 w-2.5 rounded-sm'}`}
        aria-hidden="true"
      />
      {label}
    </span>
  );
}

function TargetBreakdown({ data }: { data: AdminUsageDto }): JSX.Element {
  const { t, locale } = useI18n();
  const maximum = Math.max(1, ...data.byTarget.map((item) => item.total));
  return (
    <section className="kitup-card p-4 sm:p-5" aria-labelledby="usage-targets">
      <h2 id="usage-targets" className="font-semibold">
        {t.admin.usage.byTarget}
      </h2>
      {data.byTarget.length === 0 ? (
        <p className="mt-3 text-sm text-[var(--kitup-color-muted)]">{t.admin.usage.empty}</p>
      ) : (
        <ul className="mt-4 grid gap-3">
          {data.byTarget.map((item) => (
            <li key={item.targetType}>
              <div className="mb-1 flex justify-between gap-3 text-sm">
                <span>
                  {t.admin.targets[item.targetType as keyof typeof t.admin.targets] ??
                    item.targetType}
                </span>
                <span className="font-medium">{integer(item.total, locale)}</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-[var(--kitup-color-muted)]/10">
                <div
                  className="h-full rounded-full bg-violet-400"
                  style={{ width: `${(item.total / maximum) * 100}%` }}
                />
              </div>
              <p className="mt-1 text-xs text-[var(--kitup-color-muted)]">
                {t.admin.usage.targetDetail(
                  item.regenerations,
                  item.failed,
                  decimal(item.costUnits, locale),
                )}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TopUsers({ data }: { data: AdminUsageDto }): JSX.Element {
  const { t, locale } = useI18n();
  return (
    <section className="kitup-card overflow-hidden" aria-labelledby="usage-users">
      <h2 id="usage-users" className="p-4 pb-2 font-semibold sm:px-5">
        {t.admin.usage.topUsers}
      </h2>
      {data.topUsers.length === 0 ? (
        <p className="px-5 pb-5 text-sm text-[var(--kitup-color-muted)]">{t.admin.usage.empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[620px] text-left text-sm">
            <thead className="text-xs text-[var(--kitup-color-muted)]">
              <tr>
                <th className="px-5 py-2 font-medium">{t.admin.usage.user}</th>
                <th className="px-3 py-2 font-medium">{t.admin.usage.generationsShort}</th>
                <th className="px-3 py-2 font-medium">{t.admin.usage.regenerationsShort}</th>
                <th className="px-3 py-2 font-medium">{t.admin.usage.errorsShort}</th>
                <th className="px-5 py-2 font-medium">{t.admin.usage.cost}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--kitup-color-muted)]/15">
              {data.topUsers.map((item) => (
                <tr key={item.id}>
                  <td className="px-5 py-3">
                    <span className="block font-medium">{item.name}</span>
                    <span className="block text-xs text-[var(--kitup-color-muted)]">
                      {item.email}
                    </span>
                  </td>
                  <td className="px-3 py-3">{integer(item.total, locale)}</td>
                  <td className="px-3 py-3">{integer(item.regenerations, locale)}</td>
                  <td className={item.failed > 0 ? 'px-3 py-3 text-rose-700' : 'px-3 py-3'}>
                    {integer(item.failed, locale)}
                  </td>
                  <td className="px-5 py-3">{decimal(item.costUnits, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function integer(value: number, locale: string): string {
  return value.toLocaleString(locale);
}
function decimal(value: string, locale: string): string {
  return Number(value).toLocaleString(locale, { maximumFractionDigits: 4 });
}
function latency(value: number | null, locale: string): string {
  return value === null
    ? '—'
    : `${(value / 1000).toLocaleString(locale, { maximumFractionDigits: 1 })} s`;
}
function percentOf(part: number, total: number, locale: string): string {
  return (total === 0 ? 0 : part / total).toLocaleString(locale, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
}
